import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentStatus,
  GoalIssue,
  GoalType,
  ReviewChain,
  SelfAssessmentState,
  WindowOverride,
} from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';
import type { AssignSkipReason } from '@/lib/kra-sets-api';
import { useCurrentUser } from '@/lib/session';

/**
 * What a goal is called on screen, for the signed-in viewer.
 *
 * The title ("Engineering - Customer Engagement") is the KRA library's filing label - it
 * is there for HR picking what to assign. Employees and managers read the description,
 * which says what the goal actually asks. Falls back to the title when there is no
 * description: a goal typed in by hand, or a development goal.
 */
export function useGoalHeading() {
  const { role } = useCurrentUser();
  const hr = role === 'HR' || role === 'SUPER_ADMIN';
  return (goal: { title: string; description: string | null }) =>
    hr || !goal.description
      ? { heading: goal.title, sub: goal.description }
      : { heading: goal.description, sub: null };
}

export interface Kra {
  id: string;
  name: string;
  description: string | null;
  /**
   * This KRA's share of its goal. 0 on every KRA under a goal means the split is unused
   * and they count equally, so the figure is only worth showing when some KRA has one.
   */
  weightage: number;
}

export interface GoalUpdate {
  id: string;
  note: string;
  progressPct: number | null;
  authorName: string;
  createdAt: string;
}

export interface Goal {
  id: string;
  type: GoalType;
  title: string;
  description: string | null;
  weightage: number;
  kras: Kra[];
  /** Progress logged during the cycle, newest first. Append-only. */
  updates: GoalUpdate[];
  /** The latest logged percentage, or null if none carried one. */
  progressPct: number | null;
  /** The viewer's own unsent update, if they left one - never anyone else's. */
}

export interface GoalSet {
  assignmentId: string;
  employeeId: string;
  employeeName: string;
  cycleId: string;
  cycleLabel: string;
  /**
   * PENDING_ACCEPTANCE is legacy only — nothing new lands there now that publishing
   * makes a set live at once. Anything past DRAFT is live.
   */
  status: 'DRAFT' | 'PENDING_ACCEPTANCE' | 'ACCEPTED';
  assignedByName: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  goals: Goal[];
  validation: { ok: boolean; issues: GoalIssue[]; orgTotalWeightage: number; orgCount: number };
  changeRequestNote: string | null;
  changeRequestedAt: string | null;
  canEditOrganizational: boolean;
  canEditIndividual: boolean;
  canSend: boolean;
  canRequestChanges: boolean;
  /** A KRA under this set has been rated, so it is HR's only from here. */
  locked: boolean;
}

export interface Quarter {
  id: string;
  index: 1 | 2 | 3 | 4;
  label: string;
  startsOn: string;
  endsOn: string;
  status: string;
  window: SelfAssessmentState;
  forcedState: WindowOverride;
  forcedByName: string | null;
  forcedAt: string | null;
  forcedReason: string | null;
  frozen: boolean;
  frozenByName: string | null;
  frozenAt: string | null;
  chain: ReviewChain;
  /** False once somebody has submitted into the quarter - the flow is settled then. */
  chainEditable: boolean;
}

export interface Cycle {
  id: string;
  label: string;
  fyStartYear: number;
  startsOn: string;
  endsOn: string;
  status: string;
  quarters: Quarter[];
  currentQuarterIndex: 1 | 2 | 3 | 4 | null;
}

/** A row of the HR goal-setup overview. NOT_STARTED = no assignment row exists yet. */
export interface GoalSetSummary {
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  /** Null exactly when NOT_STARTED — there is no set to publish yet. */
  assignmentId: string | null;
  departmentName: string | null;
  /** Departments nest: "Development under Technology", not a second field. */
  parentDepartmentName: string | null;
  /** What a KRA group is pinned to, so the reader sees which group applies. */
  designationTitle: string | null;
  reportingManagerName: string | null;
  status: GoalSet['status'] | 'NOT_STARTED';
  /** This quarter's self-assessment; NOT_STARTED when there is no row yet. */
  assessmentStatus: AssessmentStatus;
  orgGoalCount: number;
  orgTotalWeightage: number;
  individualCount: number;
  /** The KRA group this set was built from, if it was. */
  assignedFromName: string | null;
  /** Resolved server-side with the same rule the assign refuses on. */
  canAssign: boolean;
  blockedReason: AssignSkipReason | null;
  blockedMessage: string | null;
  /** May the assigned goals be taken back off? Not the inverse of canAssign. */
  canUnassign: boolean;
  unassignBlockedMessage: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
}

export function useGoalOverview() {
  return useQuery<GoalSetSummary[], ApiError>({
    queryKey: ['goal-sets', 'overview'],
    queryFn: () => api.get<GoalSetSummary[]>('/goal-sets'),
  });
}

/** One row of what a bulk publish did. `published: false` always carries a message. */
export interface PublishOutcome {
  assignmentId: string;
  employeeId: string | null;
  employeeName: string;
  published: boolean;
  reason: string | null;
  message: string | null;
}

/**
 * Publish a batch. Returns a row per employee rather than a bare success — the ones it
 * skipped are the whole reason to look at the result.
 */
export function usePublishMany() {
  const queryClient = useQueryClient();
  return useMutation<PublishOutcome[], ApiError, string[]>({
    mutationFn: (assignmentIds) =>
      api.post<PublishOutcome[]>('/goal-sets/publish', { assignmentIds }),
    onSuccess: (outcomes) => {
      void queryClient.invalidateQueries({ queryKey: ['goal-sets', 'overview'] });
      // Anyone whose set just went live has a stale per-employee cache behind the
      // Open link, and a stale assessment gate keyed on ACCEPTED.
      for (const outcome of outcomes.filter((o) => o.published && o.employeeId)) {
        void queryClient.invalidateQueries({ queryKey: goalSetKey(outcome.employeeId!) });
      }
      void queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });
}

const goalSetKey = (employeeId: string | 'me') => ['goal-set', employeeId] as const;

export function useGoalSet(employeeId: string | 'me') {
  return useQuery<GoalSet, ApiError>({
    queryKey: goalSetKey(employeeId),
    queryFn: () =>
      api.get<GoalSet>(employeeId === 'me' ? '/goal-sets/me' : `/goal-sets/employee/${employeeId}`),
  });
}

export function useCurrentCycle() {
  return useQuery<Cycle, ApiError>({
    queryKey: ['cycle', 'current'],
    queryFn: () => api.get<Cycle>('/cycles/current'),
    staleTime: 5 * 60_000,
  });
}

/** Every financial year, newest first. HR only needs this to set future ones up. */
export function useCycles() {
  return useQuery<Cycle[], ApiError>({
    queryKey: ['cycles'],
    queryFn: () => api.get<Cycle[]>('/cycles'),
  });
}

/**
 * Every goal mutation returns the whole updated set, so the cache is replaced from
 * the response. That keeps the weightage meter and the validation issues in step
 * with the server in one round trip - recomputing them client-side is how the two
 * end up disagreeing about whether Send is allowed.
 */
function useGoalMutation<TArgs>(
  employeeId: string | 'me',
  fn: (args: TArgs) => Promise<GoalSet>,
) {
  const queryClient = useQueryClient();
  return useMutation<GoalSet, ApiError, TArgs>({
    mutationFn: fn,
    onSuccess: (next) => {
      queryClient.setQueryData(goalSetKey(employeeId), next);
      void queryClient.invalidateQueries({ queryKey: ['people'] });
      // The assessment draws these goals, their KRAs and their progress log too.
      void queryClient.invalidateQueries({ queryKey: ['assessment'] });
    },
  });
}

export interface NewGoal {
  type: GoalType;
  title: string;
  description?: string | null;
  weightage: number;
}

export function useAddGoal(employeeId: string | 'me', assignmentId: string) {
  return useGoalMutation<NewGoal>(employeeId, (body) =>
    api.post<GoalSet>(`/goal-sets/${assignmentId}/goals`, body),
  );
}

export function useUpdateGoal(employeeId: string | 'me') {
  return useGoalMutation<{ goalId: string; title?: string; weightage?: number; description?: string | null }>(
    employeeId,
    ({ goalId, ...body }) => api.patch<GoalSet>(`/goal-sets/goals/${goalId}`, body),
  );
}

export function useDeleteGoal(employeeId: string | 'me') {
  return useGoalMutation<string>(employeeId, (goalId) =>
    api.delete<GoalSet>(`/goal-sets/goals/${goalId}`),
  );
}

export function useAddKra(employeeId: string | 'me') {
  return useGoalMutation<{ goalId: string; name: string; description?: string | null }>(
    employeeId,
    ({ goalId, ...body }) => api.post<GoalSet>(`/goal-sets/goals/${goalId}/kras`, body),
  );
}

export function useDeleteKra(employeeId: string | 'me') {
  return useGoalMutation<string>(employeeId, (kraId) =>
    api.delete<GoalSet>(`/goal-sets/kras/${kraId}`),
  );
}

/** Makes the set live. There is no acceptance step for the employee to hold up. */
export function usePublishGoals(employeeId: string | 'me', assignmentId: string) {
  return useGoalMutation<void>(employeeId, () =>
    api.post<GoalSet>(`/goal-sets/${assignmentId}/publish`),
  );
}

export function useRequestGoalChanges(employeeId: string | 'me', assignmentId: string) {
  return useGoalMutation<{ note: string }>(employeeId, (body) =>
    api.post<GoalSet>(`/goal-sets/${assignmentId}/request-changes`, body),
  );
}
